import { useDashboardPermissions } from '@components/utils/DashboardPermissions';
import { getNumberString } from '@fourthwall/components';
import { formatMoneyValue, getPaymentMethodLabel, getValue, mapChartToSeriesData, mapRangeToText, mapSaleByCountryTableDataItemToAnalyticsRow, TABLE_QUERIES_LIMIT, useAnalyticsChartQuery, useMembershipsChartQuery, useMembershipsTopTiersTableQuery, usePaymentMethodUsageTableQuery, useSaleByCountryTableQuery, useShopsStatsTrafficQuery, useTopOfferTableQuery, } from '@fourthwall/module-analytics';
import { getPrice } from '@fourthwall/utils/dist/common';
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop';
import { useMembershipAccountQuery } from '@modules/Dashboard/hooks/useMembershipAccountQuery';
import { lowerCase, startCase } from 'lodash-es';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { REPORT_TYPES } from '../../../consts';
import { useGoogleAnalyticsConfigurationQuery } from '../modules/GoogleAnalytics';
import { createGetReportTo } from '../utils/createGetReportTo';
import { getEndDateFromRange } from '../utils/getEndDateFromRange';
import { getSocialSourceLabel } from '../utils/getSocialSourceLabel';
import { mapConversionRatesAggregatedValueToAnalyticsRows } from '../utils/mapConversionRatesAggregatedValueToAnalyticsRows';
import { mapOffersToAnalyticsRows } from '../utils/mapOffersToAnalyticsRows';
import { mapTopTiersToAnalyticsRows } from '../utils/mapTopTiersToAnalyticsRows';
import { prepareSingleValueChart } from '../utils/prepareSingleValueChart';
export const useAnalyticsOverview = (params) => {
    const { hasPermission } = useDashboardPermissions();
    const { currentShop } = useCurrentShop({ staleTime: Infinity });
    const { googleAnalyticsConfigurationQuery } = useGoogleAnalyticsConfigurationQuery();
    const isGoogleAnalyticsConnected = googleAnalyticsConfigurationQuery.data?.type === 'CONNECTED';
    const membershipsEnabled = !!currentShop?.settings.memberships?.enabled;
    const { analyticsChartQuery: profitQuery } = useAnalyticsChartQuery([
        { ...params, chartEntityGroup: undefined, chartTypes: ['PROFIT'], sid: [currentShop?.id] },
    ]);
    const { analyticsChartQuery: contributionQuery } = useAnalyticsChartQuery([
        {
            ...params,
            chartEntityGroup: undefined,
            chartTypes: ['CONTRIBUTION'],
            sid: [currentShop?.id],
        },
    ]);
    const { analyticsChartQuery: contributionAvgQuery } = useAnalyticsChartQuery([
        {
            ...params,
            chartEntityGroup: undefined,
            chartTypes: ['CONTRIBUTION', 'CONTRIBUTION_AVG', 'GROSS_SALES'],
            sid: [currentShop?.id],
        },
    ]);
    const { membershipsChartQuery: membersTotalQuery } = useMembershipsChartQuery([
        {
            ...params,
            tiersIds: undefined,
            intervals: undefined,
            devices: undefined,
            chartTypes: ['MEMBERS_TOTAL'],
            sid: [currentShop?.id],
        },
    ], { enabled: membershipsEnabled });
    const { membershipsChartQuery: membersTotalFreeQuery } = useMembershipsChartQuery([
        {
            ...params,
            tiersIds: undefined,
            intervals: undefined,
            devices: undefined,
            chartTypes: ['FREE_ACCOUNTS_TOTAL'],
            sid: [currentShop?.id],
        },
    ], { enabled: membershipsEnabled });
    const { membershipsChartQuery: membersChangeQuery } = useMembershipsChartQuery([
        {
            ...params,
            tiersIds: undefined,
            intervals: undefined,
            devices: undefined,
            chartTypes: ['MEMBERS_CHANGE'],
            sid: [currentShop?.id],
        },
    ], { enabled: membershipsEnabled });
    const { membershipsChartQuery: freeMembersChangeQuery } = useMembershipsChartQuery([
        {
            ...params,
            tiersIds: undefined,
            intervals: undefined,
            devices: undefined,
            chartTypes: ['FREE_ACCOUNTS_CHANGE'],
            sid: [currentShop?.id],
        },
    ], { enabled: membershipsEnabled });
    const { topOfferTableQuery } = useTopOfferTableQuery([
        { ...params, limit: TABLE_QUERIES_LIMIT, sid: [currentShop?.id] },
    ]);
    const { saleByCountryTableQuery } = useSaleByCountryTableQuery([
        { ...params, limit: TABLE_QUERIES_LIMIT, sid: [currentShop?.id] },
    ]);
    const { membershipsTopTiersTableQuery } = useMembershipsTopTiersTableQuery([{ ...params, limit: TABLE_QUERIES_LIMIT, sid: [currentShop?.id] }], { enabled: membershipsEnabled });
    const { membershipAccountQuery } = useMembershipAccountQuery();
    const { paymentMethodUsageTableQuery } = usePaymentMethodUsageTableQuery([
        { ...params, sid: [currentShop?.id] },
    ]);
    const { shopsStatsTrafficQuery } = useShopsStatsTrafficQuery([{ ...params }], {
        enabled: isGoogleAnalyticsConnected,
    });
    const location = useLocation();
    const getReportTo = useMemo(() => createGetReportTo(location), [location]);
    const profitChart = useMemo(() => {
        if (!hasPermission('analytics.profit'))
            return;
        return {
            title: REPORT_TYPES.profit.title,
            reportTo: getReportTo(REPORT_TYPES.profit.slug),
            range: mapRangeToText(profitQuery.data?.current.range),
            value: getValue(profitQuery.data?.current.profit),
            series: [
                {
                    label: REPORT_TYPES.profit.title,
                    data: mapChartToSeriesData(profitQuery.data?.current.profit?.data, params),
                },
            ],
            isLoading: profitQuery.isLoading,
            isError: profitQuery.isError,
            formatDate: REPORT_TYPES.profit.getDateFormatter(params.precision),
            formatValue: REPORT_TYPES.profit.formatValue,
        };
    }, [getReportTo, profitQuery, params, hasPermission]);
    const membershipsTotalMembersChart = useMemo(() => {
        if (!membershipsEnabled || !hasPermission('analytics.totalMembers')) {
            return undefined;
        }
        return {
            title: REPORT_TYPES['memberships-total-members'].title,
            reportTo: getReportTo(REPORT_TYPES['memberships-total-members'].slug),
            range: mapRangeToText(membersTotalQuery.data?.current.range),
            value: getValue(membersTotalQuery.data?.current.totalMembers),
            series: [
                {
                    label: REPORT_TYPES['memberships-total-members'].title,
                    data: mapChartToSeriesData(membersTotalQuery.data?.current.totalMembers?.data, params),
                },
            ],
            isLoading: membersTotalQuery.isLoading,
            isError: membersTotalQuery.isError,
            formatDate: REPORT_TYPES['memberships-total-members'].getDateFormatter(params.precision),
            formatValue: REPORT_TYPES['memberships-total-members'].formatValue,
        };
    }, [getReportTo, membersTotalQuery, params, membershipsEnabled, hasPermission]);
    const membershipsTotalFreeMembersChart = useMemo(() => {
        if (!membershipsEnabled || !hasPermission('analytics.totalMembers')) {
            return undefined;
        }
        return {
            title: REPORT_TYPES['memberships-total-free-members'].title,
            tooltip: REPORT_TYPES['memberships-total-free-members'].tooltip,
            reportTo: getReportTo(REPORT_TYPES['memberships-total-free-members'].slug),
            range: mapRangeToText(membersTotalFreeQuery.data?.current.range),
            value: getValue(membersTotalFreeQuery.data?.current.totalFreeAccounts),
            series: [
                {
                    label: REPORT_TYPES['memberships-total-free-members'].title,
                    data: mapChartToSeriesData(membersTotalFreeQuery.data?.current.totalFreeAccounts?.data, params),
                },
            ],
            isLoading: membersTotalFreeQuery.isLoading,
            isError: membersTotalFreeQuery.isError,
            formatDate: REPORT_TYPES['memberships-total-free-members'].getDateFormatter(params.precision),
            formatValue: REPORT_TYPES['memberships-total-free-members'].formatValue,
        };
    }, [getReportTo, membersTotalFreeQuery, params, membershipsEnabled, hasPermission]);
    const membershipsMembersChangeChart = useMemo(() => {
        const getMembershipsMembersChangeValue = () => {
            const value = getValue(membersChangeQuery.data?.current.membersChange);
            if (!value || typeof value !== 'number') {
                return 0;
            }
            return value <= 0 ? value : `+${value}`;
        };
        if (!membershipsEnabled || !hasPermission('analytics.membersChange')) {
            return undefined;
        }
        return {
            title: REPORT_TYPES['memberships-members-change'].title,
            reportTo: getReportTo(REPORT_TYPES['memberships-members-change'].slug),
            range: mapRangeToText(membersChangeQuery.data?.current.range),
            value: getMembershipsMembersChangeValue(),
            series: [
                {
                    label: REPORT_TYPES['memberships-members-change'].title,
                    data: mapChartToSeriesData(prepareSingleValueChart(membersChangeQuery.data?.current.membersChange?.data), params),
                },
            ],
            isLoading: membersChangeQuery.isLoading,
            isError: membersChangeQuery.isError,
            formatDate: REPORT_TYPES['memberships-members-change'].getDateFormatter(params.precision),
            formatValue: REPORT_TYPES['memberships-members-change'].formatValue,
        };
    }, [getReportTo, membersChangeQuery, params, membershipsEnabled, hasPermission]);
    const membershipsFreeMembersChangeChart = useMemo(() => {
        const getMembershipsFreeMembersChangeValue = () => {
            const value = getValue(freeMembersChangeQuery.data?.current.freeAccountsChange);
            if (!value || typeof value !== 'number') {
                return 0;
            }
            return value <= 0 ? value : `+${value}`;
        };
        if (!membershipsEnabled || !hasPermission('analytics.membersChange')) {
            return undefined;
        }
        return {
            title: REPORT_TYPES['memberships-free-members-change'].title,
            reportTo: getReportTo(REPORT_TYPES['memberships-free-members-change'].slug),
            range: mapRangeToText(freeMembersChangeQuery.data?.current.range),
            value: getMembershipsFreeMembersChangeValue(),
            series: [
                {
                    label: REPORT_TYPES['memberships-free-members-change'].title,
                    data: mapChartToSeriesData(prepareSingleValueChart(freeMembersChangeQuery.data?.current.freeAccountsChange?.data), params),
                },
            ],
            isLoading: freeMembersChangeQuery.isLoading,
            isError: freeMembersChangeQuery.isError,
            formatDate: REPORT_TYPES['memberships-free-members-change'].getDateFormatter(params.precision),
            formatValue: REPORT_TYPES['memberships-free-members-change'].formatValue,
        };
    }, [getReportTo, freeMembersChangeQuery, params, membershipsEnabled, hasPermission]);
    const contributionsChart = useMemo(() => {
        if (!hasPermission('analytics.contributions'))
            return;
        return {
            title: REPORT_TYPES.orders.title,
            reportTo: getReportTo(REPORT_TYPES.orders.slug),
            range: mapRangeToText(contributionQuery.data?.current.range),
            value: getValue(contributionQuery.data?.current.contribution),
            series: [
                {
                    label: REPORT_TYPES.orders.title,
                    data: mapChartToSeriesData(contributionQuery.data?.current.contribution?.data, params),
                },
            ],
            isLoading: contributionQuery.isLoading,
            isError: contributionQuery.isError,
            formatDate: REPORT_TYPES.orders.getDateFormatter(params.precision),
            formatValue: REPORT_TYPES.orders.formatValue,
        };
    }, [getReportTo, contributionQuery, params, hasPermission]);
    const contributionValueChart = useMemo(() => {
        if (!hasPermission('analytics.averageContributionValue'))
            return;
        return {
            title: REPORT_TYPES['average-order-value'].title,
            reportTo: getReportTo(REPORT_TYPES['average-order-value'].slug),
            range: mapRangeToText(contributionAvgQuery.data?.current.range),
            value: getValue(contributionAvgQuery.data?.current.averageGrossSalesValue),
            series: [
                {
                    label: REPORT_TYPES['average-order-value'].title,
                    data: mapChartToSeriesData(contributionAvgQuery.data?.current.contributionAvg?.data, params),
                },
            ],
            isLoading: contributionAvgQuery.isLoading,
            isError: contributionAvgQuery.isError,
            formatDate: REPORT_TYPES['average-order-value'].getDateFormatter(params.precision),
            formatValue: REPORT_TYPES['average-order-value'].formatValue,
        };
    }, [getReportTo, contributionAvgQuery, params, hasPermission]);
    const topProductsChart = useMemo(() => {
        if (!hasPermission('analytics.topProducts'))
            return;
        return {
            title: REPORT_TYPES['top-products-by-units-sold'].title,
            reportTo: getReportTo(REPORT_TYPES['top-products-by-units-sold'].slug),
            range: mapRangeToText(topOfferTableQuery.data?.current.range),
            rows: mapOffersToAnalyticsRows(topOfferTableQuery.data?.current.data),
            isLoading: topOfferTableQuery.isLoading,
            isError: topOfferTableQuery.isError,
            formatValue: formatMoneyValue,
        };
    }, [getReportTo, topOfferTableQuery, hasPermission]);
    const salesByCountryChart = useMemo(() => {
        if (!hasPermission('analytics.salesByCountry'))
            return;
        return {
            title: REPORT_TYPES['sales-by-country'].title,
            reportTo: getReportTo(REPORT_TYPES['sales-by-country'].slug),
            range: mapRangeToText(saleByCountryTableQuery.data?.current.range),
            rows: (saleByCountryTableQuery.data?.current.data || []).map(mapSaleByCountryTableDataItemToAnalyticsRow),
            isLoading: saleByCountryTableQuery.isLoading,
            isError: saleByCountryTableQuery.isError,
            formatValue: formatMoneyValue,
        };
    }, [getReportTo, saleByCountryTableQuery, hasPermission]);
    const membershipsTopTiersChart = useMemo(() => {
        if (!membershipsEnabled || !hasPermission('analytics.topTiers'))
            return;
        return {
            title: REPORT_TYPES['membership-top-tiers'].title,
            reportTo: getReportTo(REPORT_TYPES['membership-top-tiers'].slug),
            range: getEndDateFromRange(membershipsTopTiersTableQuery.data?.current.range),
            rows: mapTopTiersToAnalyticsRows(membershipsTopTiersTableQuery.data?.current.data, membershipAccountQuery.data?.tiers),
            isLoading: membershipsTopTiersTableQuery.isLoading,
            isError: membershipsTopTiersTableQuery.isError,
            formatValue: formatMoneyValue,
        };
    }, [
        getReportTo,
        membershipsTopTiersTableQuery,
        membershipAccountQuery.data?.tiers,
        membershipsEnabled,
        hasPermission,
    ]);
    const paymentMethodUsedChart = useMemo(() => ({
        title: REPORT_TYPES['payment-method-used'].title,
        reportTo: getReportTo(REPORT_TYPES['payment-method-used'].slug),
        range: mapRangeToText(paymentMethodUsageTableQuery.data?.current.range),
        barChart: {
            data: paymentMethodUsageTableQuery.data?.current.data || [],
            // @ts-ignore // TODO
            getLabel: getPaymentMethodLabel,
            // @ts-ignore // TODO
            getValue: (item) => item.percentage,
            // @ts-ignore // TODO
            getValueLabel: (item) => `${item.percentage}%`,
            totalValue: paymentMethodUsageTableQuery.data?.current.totalValue,
            limit: TABLE_QUERIES_LIMIT,
        },
        isLoading: paymentMethodUsageTableQuery.isLoading,
        isError: paymentMethodUsageTableQuery.isError,
    }), [getReportTo, paymentMethodUsageTableQuery]);
    const conversionRatesChart = useMemo(() => {
        if (!isGoogleAnalyticsConnected)
            return;
        const value = shopsStatsTrafficQuery.data?.current.conversionRates?.aggregatedValue?.checkoutPaid.percentage;
        return {
            title: REPORT_TYPES['conversion-rates'].title,
            externalSource: REPORT_TYPES['conversion-rates'].externalSource,
            reportTo: getReportTo(REPORT_TYPES['conversion-rates'].slug),
            value: value ? `${value}%` : undefined,
            rows: mapConversionRatesAggregatedValueToAnalyticsRows(shopsStatsTrafficQuery.data?.current.conversionRates?.aggregatedValue),
            range: mapRangeToText(shopsStatsTrafficQuery.data?.current.conversionRates?.range),
            isLoading: shopsStatsTrafficQuery.isFetching,
            isError: shopsStatsTrafficQuery.isError,
        };
    }, [isGoogleAnalyticsConnected, shopsStatsTrafficQuery, getReportTo]);
    const uniqueVisitorsChart = useMemo(() => {
        if (!isGoogleAnalyticsConnected)
            return;
        const mainSeriesData = mapChartToSeriesData(shopsStatsTrafficQuery.data?.current.uniqueVisitors?.data, params, { addZeroEntriesForMissingDates: false });
        const hasPredictedData = Boolean(shopsStatsTrafficQuery.data?.current.uniqueVisitors?.predictedData.timestamps.length);
        const series = [
            {
                label: REPORT_TYPES['unique-visitors'].title,
                data: mainSeriesData,
            },
        ];
        if (hasPredictedData) {
            const predictedData = [
                mainSeriesData[mainSeriesData.length - 1],
                ...mapChartToSeriesData(shopsStatsTrafficQuery.data?.current.uniqueVisitors?.predictedData, params, { addZeroEntriesForMissingDates: false }),
            ];
            series.push({
                label: `${REPORT_TYPES['unique-visitors'].title} (predicted)`,
                data: predictedData,
            });
        }
        return {
            title: REPORT_TYPES['unique-visitors'].title,
            externalSource: REPORT_TYPES['conversion-rates'].externalSource,
            reportTo: getReportTo(REPORT_TYPES['unique-visitors'].slug),
            range: mapRangeToText(shopsStatsTrafficQuery.data?.current.uniqueVisitors?.range),
            value: shopsStatsTrafficQuery.data?.current.uniqueVisitors?.aggregatedValue,
            series,
            isLoading: shopsStatsTrafficQuery.isFetching,
            isError: shopsStatsTrafficQuery.isError,
            formatDate: REPORT_TYPES['unique-visitors'].getDateFormatter(params.precision),
            formatValue: REPORT_TYPES['unique-visitors'].formatValue,
        };
    }, [isGoogleAnalyticsConnected, getReportTo, shopsStatsTrafficQuery, params]);
    const salesByTrafficSourceTableChart = useMemo(() => {
        if (!isGoogleAnalyticsConnected)
            return;
        return {
            title: REPORT_TYPES['sales-by-traffic-source'].title,
            externalSource: REPORT_TYPES['conversion-rates'].externalSource,
            range: mapRangeToText(shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.range),
            reportTo: getReportTo(REPORT_TYPES['sales-by-traffic-source'].slug),
            barChart: {
                data: shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.data || [],
                // @ts-ignore
                getLabel: (item) => startCase(lowerCase(item.source)),
                // @ts-ignore
                getValue: (item) => item.income,
                // @ts-ignore
                getValueLabel: (item) => {
                    // @ts-ignore
                    return getPrice(item.income, {
                        currency: shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.types?.currency,
                    });
                },
                totalValue: shopsStatsTrafficQuery.data?.current.salesByTrafficSourceTable?.totalValue,
                limit: TABLE_QUERIES_LIMIT,
            },
            isLoading: shopsStatsTrafficQuery.isFetching,
            isError: shopsStatsTrafficQuery.isError,
        };
    }, [getReportTo, isGoogleAnalyticsConnected, shopsStatsTrafficQuery]);
    const sessionsByTrafficSourceChart = useMemo(() => {
        if (!isGoogleAnalyticsConnected)
            return;
        return {
            title: REPORT_TYPES['sessions-by-traffic-source'].title,
            externalSource: REPORT_TYPES['conversion-rates'].externalSource,
            reportTo: getReportTo(REPORT_TYPES['sessions-by-traffic-source'].slug),
            range: mapRangeToText(shopsStatsTrafficQuery.data?.current.sessionsByTrafficSource?.range),
            barChart: {
                data: shopsStatsTrafficQuery.data?.current.sessionsByTrafficSource?.data || [],
                // @ts-ignore
                getLabel: (item) => startCase(lowerCase(item.source)),
                // @ts-ignore
                getValue: (item) => item.count,
                // @ts-ignore
                getValueLabel: (item) => getNumberString(item.count),
                totalValue: shopsStatsTrafficQuery.data?.current.sessionsByTrafficSource?.totalValue,
                limit: TABLE_QUERIES_LIMIT,
            },
            isLoading: shopsStatsTrafficQuery.isFetching,
            isError: shopsStatsTrafficQuery.isError,
        };
    }, [getReportTo, isGoogleAnalyticsConnected, shopsStatsTrafficQuery]);
    const salesBySocialSourceTableChart = useMemo(() => {
        if (!isGoogleAnalyticsConnected)
            return;
        return {
            title: REPORT_TYPES['sales-by-social-source'].title,
            externalSource: REPORT_TYPES['conversion-rates'].externalSource,
            reportTo: getReportTo(REPORT_TYPES['sales-by-social-source'].slug),
            range: mapRangeToText(shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.range),
            barChart: {
                data: shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.data || [],
                // @ts-ignore
                getLabel: getSocialSourceLabel,
                // @ts-ignore
                getValue: (item) => item.income,
                // @ts-ignore
                getValueLabel: (item) => {
                    // @ts-ignore
                    return getPrice(item.income, {
                        currency: shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.types?.currency,
                    });
                },
                totalValue: shopsStatsTrafficQuery.data?.current.salesBySocialSourceTable?.totalValue,
                limit: TABLE_QUERIES_LIMIT,
            },
            isLoading: shopsStatsTrafficQuery.isFetching,
            isError: shopsStatsTrafficQuery.isError,
        };
    }, [getReportTo, isGoogleAnalyticsConnected, shopsStatsTrafficQuery]);
    const sessionsBySocialSourceChart = useMemo(() => {
        if (!isGoogleAnalyticsConnected)
            return;
        return {
            title: REPORT_TYPES['sessions-by-social-source'].title,
            externalSource: REPORT_TYPES['conversion-rates'].externalSource,
            reportTo: getReportTo(REPORT_TYPES['sessions-by-social-source'].slug),
            range: mapRangeToText(shopsStatsTrafficQuery.data?.current.sessionsBySocialSource?.range),
            barChart: {
                data: shopsStatsTrafficQuery.data?.current.sessionsBySocialSource?.data || [],
                // @ts-ignore
                getLabel: getSocialSourceLabel,
                // @ts-ignore
                getValue: (item) => item.count,
                // @ts-ignore
                getValueLabel: (item) => getNumberString(item.count),
                totalValue: shopsStatsTrafficQuery.data?.current.sessionsBySocialSource?.totalValue,
                limit: TABLE_QUERIES_LIMIT,
            },
            isLoading: shopsStatsTrafficQuery.isFetching,
            isError: shopsStatsTrafficQuery.isError,
        };
    }, [getReportTo, isGoogleAnalyticsConnected, shopsStatsTrafficQuery]);
    return {
        analyticsOverview: {
            profitChart,
            membershipsTotalMembersChart,
            membershipsTotalFreeMembersChart,
            membershipsMembersChangeChart,
            membershipsFreeMembersChangeChart,
            contributionsChart,
            contributionValueChart,
            topProductsChart,
            salesByCountryChart,
            membershipsTopTiersChart,
            paymentMethodUsedChart,
            conversionRatesChart,
            uniqueVisitorsChart,
            salesByTrafficSourceTableChart,
            sessionsByTrafficSourceChart,
            salesBySocialSourceTableChart,
            sessionsBySocialSourceChart,
        },
    };
};
